/* App Studio shell — full-viewport studio chrome: top bar + ONE vertical
 * sidebar (App · Build · Activity) + one content scroll region. The active
 * item is derived from the path tail. */
import React, {
    useEffect,
    useState
} from 'react';
import {
    Outlet,
    useLocation,
    useNavigate
} from 'react-router-dom';
import {
    Activity,
    Boxes,
    ExternalLink,
    FileJson,
    LayoutDashboard,
    Moon,
    Play,
    Sun,
    X
} from 'lucide-react';
import {
    AppStudioProvider,
    useAppStudio
} from './AppStudioContext';
import {
    openAppUrl
} from './shared/openAppUrl';
import BuildRail from './screens/walkthrough/BuildRail';
import {
    readStoredMode,
    storeMode
} from './ui/themeMode';
import './ui/tokens.css';
import './ui/shell.css';

function ShellChrome() {
    const {
        orgId,
        appId,
        confirmLeave
    } = useAppStudio();
    const nav = useNavigate();
    const {
        pathname
    } = useLocation();
    const [mode, setMode] = useState<string>(readStoredMode);

    // Mirror theme attributes on <body> for portaled Radix content.
    useEffect(() => {
        document.body.setAttribute('data-theme', 'appstudio');
        document.body.setAttribute('data-aps-mode', mode);
        return () => {
            document.body.removeAttribute('data-theme');
            document.body.removeAttribute('data-aps-mode');
        };
    }, [mode]);

    const toggleMode = () => {
        const next = mode === 'dark' ? 'light' : 'dark';
        setMode(next);
        storeMode(next);
    };

    const go = (to: string) => {
        if (!confirmLeave()) return;
        nav(to);
    };

    const tailSeg = pathname.split('/').filter(Boolean).pop();

    return (
        <div className="aps-root aps-root-embedded" data-theme="appstudio" data-aps-mode={mode}>
            <header className="aps-topbar">
                <button className="aps-brand" onClick={() => go(`/organizations/${orgId}/3dapp-studio`)}>
                    <span className="aps-brand-mark"><Boxes size={15} /></span>
                    3D App Studio
                </button>
                <nav className="aps-crumbs" aria-label="Breadcrumb">
                    <button className="aps-crumb" onClick={() => go(`/organizations/${orgId}/3dapp-studio`)}>
                        apps
                    </button>
                    <span className="aps-crumb-sep">/</span>
                    <span className="aps-crumb current">{appId}</span>
                </nav>
                <span className="aps-topbar-spacer" />
                <button className="aps-icon-btn" title="Toggle light / dark" onClick={toggleMode}>
                    {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                </button>
                <a
                    className="aps-btn aps-btn-primary aps-btn-sm"
                    href={openAppUrl(orgId, appId)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open the running app in the 3D runtime"
                >
                    <ExternalLink size={14} />
                    Open App
                </a>
                <button className="aps-icon-btn" title="Exit 3D App Studio"
                    onClick={() => go(`/organizations/${orgId}/3dapp-studio`)}>
                    <X size={16} />
                </button>
            </header>
            <div className="aps-body">
                <aside className="aps-rail">
                    <button
                        className={'aps-rail-link' + (tailSeg === 'overview' ? ' active' : '')}
                        onClick={() => go('overview')}
                    >
                        <LayoutDashboard size={16} />
                        <span>App</span>
                    </button>
                    <button
                        className={'aps-rail-link' + (tailSeg === 'json' ? ' active' : '')}
                        onClick={() => go('json')}
                    >
                        <FileJson size={16} />
                        <span>App JSON</span>
                    </button>
                    <BuildRail tailSeg={tailSeg} go={go} />
                    <span className="aps-rail-section aps-meta">Activity</span>
                    <button
                        className={'aps-rail-link' + (tailSeg === 'runs' ? ' active' : '')}
                        onClick={() => go('runs')}
                    >
                        <Play size={16} />
                        <span>Runs</span>
                    </button>
                    <button
                        className={'aps-rail-link' + (tailSeg === 'workflow-runs' ? ' active' : '')}
                        onClick={() => go('workflow-runs')}
                    >
                        <Activity size={16} />
                        <span>Workflow runs</span>
                    </button>
                </aside>
                <main className="aps-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

export default function AppStudioShell() {
    return (
        <AppStudioProvider>
            <ShellChrome />
        </AppStudioProvider>
    );
}
